// Pages/inventario/components/RecomprarBuscador.tsx
//
// «¿Ya lo compraste antes?» — buscador de recompra (round 2026-08-06).
//
// ── El problema ─────────────────────────────────────────────────────────────
//
// Volver a comprar algo que ya existía obligaba a re-tipear la ficha entera
// —nombre, marca, color, material, formato— de memoria y sin ver el producto.
// Cualquier diferencia mínima en cualquiera de esos campos nacía como un
// producto DISTINTO.
//
// Ese es el mecanismo real por el que el inventario se llenó de duplicados: no
// por descuido de nadie, sino porque el formulario pedía reconstruir a ciegas
// algo que el sistema ya sabía. La estandarización del texto
// (utils/etiquetaTexto.js) tapa las variantes de grafía; esto ataca la causa un
// paso antes — que no haya que volver a escribirlo.
//
// ── Qué prellena y qué no ───────────────────────────────────────────────────
//
// Prellena la FICHA: qué es la cosa. No toca cantidades, precios, destino ni
// boleta, que es lo que esta compra sí trae de nuevo.
//
// El precio se MUESTRA como referencia pero no se escribe. Prellenarlo sería
// cómodo el 90 % de las veces y desastroso el otro 10: es el dato que cambia de
// compra a compra y el único que corrompe las finanzas si entra mal sin que
// nadie lo mire. Un número ya escrito en la casilla no se revisa.
//
// Lo que se muestra es el PRECIO ACTUAL del producto, que desde «un producto,
// un precio» es el promedio ponderado de todas sus compras — no el de la última.
// La primera versión decía «la última vez costó X» y era falso por partida
// doble: con dos compras de 100 unidades a $5.000 y 5 a $12.000, el cartel
// habría dicho $5.905, que no corresponde a ninguna boleta y está muy por
// debajo de lo que hoy cuesta reponerlo. Justo en el cartel que existe para que
// el admin contraste el precio que está por escribir.
import React, { useCallback, useRef, useState } from "react";
import { Alert, Button, Select, Space, Spin, Tag, Typography } from "antd";
import { ShoppingOutlined } from "@ant-design/icons";
import { inventarioApi } from "../../../services/inventario.api";
import { formatPropertyName } from "../../../utils/propertyDisplay";
import "../inventario.css";

const { Text } = Typography;

const CLP = (n: number | null | undefined) =>
  `$${Number(n || 0).toLocaleString("es-CL")}`;

export interface ProductoRecompra {
  id: number;
  nombre: string;
  categoria: string | null;
  marca: string | null;
  modelo: string | null;
  sku: string | null;
  material: string | null;
  color: string | null;
  forma: string | null;
  diseno: string | null;
  tamano: string | null;
  volumenValor: number | null;
  volumenUnidad: string | null;
  pesoValor: number | null;
  pesoUnidad: string | null;
  esPack: boolean;
  unidadesPorPack: number;
  /** Precio actual del producto = promedio ponderado de TODAS sus compras. */
  precioUnidad: number;
  precioPack: number;
  oculto: boolean;
  ubicacionTipo: string | null;
  ubicacionNombre: string | null;
  miniatura: string | null;
}

interface Props {
  /** Producto elegido, o null si se está creando desde cero. */
  elegido: ProductoRecompra | null;
  onElegir: (producto: ProductoRecompra) => void;
  onLimpiar: () => void;
}

const RecomprarBuscador: React.FC<Props> = ({ elegido, onElegir, onLimpiar }) => {
  const [opciones, setOpciones] = useState<ProductoRecompra[]>([]);
  const [buscando, setBuscando] = useState(false);
  // Token de secuencia: al escribir rápido salen varias consultas y no vuelven
  // necesariamente en orden. Sin esto, una respuesta vieja pisa a la nueva y el
  // desplegable muestra resultados de lo que se escribió hace dos teclas.
  const seqRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buscar = useCallback((texto: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const limpio = texto.trim();
    if (limpio.length < 2) {
      setOpciones([]);
      setBuscando(false);
      return;
    }
    setBuscando(true);
    debounceRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      const res = await inventarioApi.buscarProductos(limpio);
      if (seq !== seqRef.current) return;
      setOpciones(Array.isArray(res?.data) ? res.data : []);
      setBuscando(false);
    }, 300);
  }, []);

  if (elegido) {
    const precio = elegido.esPack ? elegido.precioPack : elegido.precioUnidad;
    return (
      <Alert
        type="info"
        showIcon={false}
        className="inventario-recompra-elegido"
        message={
          <div className="inventario-recompra-ficha">
            {elegido.miniatura ? (
              <img
                className="inventario-miniatura"
                src={elegido.miniatura}
                alt={elegido.nombre}
              />
            ) : null}
            <div className="inventario-recompra-datos">
              <div>
                <Tag className="inventario-id-tag">#{elegido.id}</Tag>{" "}
                <strong>{elegido.nombre}</strong>
                {elegido.marca ? <Text type="secondary"> · {elegido.marca}</Text> : null}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Ficha copiada. Falta completar cantidad, precio y destino de{" "}
                <em>esta</em> compra.
                {precio > 0 && (
                  <>
                    {" "}
                    Hoy está valorizado en <strong>{CLP(precio)}</strong> por{" "}
                    {elegido.esPack ? "pack" : "unidad"} (promedio de sus
                    compras).
                  </>
                )}
              </Text>
            </div>
            <Button size="small" onClick={onLimpiar}>
              Empezar de cero
            </Button>
          </div>
        }
      />
    );
  }

  return (
    <div className="inventario-recompra-buscador">
      <Select
        showSearch
        allowClear
        value={null}
        style={{ width: "100%" }}
        placeholder="¿Ya lo compraste antes? Búscalo y se copia su ficha"
        suffixIcon={<ShoppingOutlined />}
        // El filtrado lo hace el servidor (búsqueda difusa sobre nombre, marca,
        // modelo y SKU). Filtrar además acá escondería resultados que el
        // servidor sí consideró parecidos.
        filterOption={false}
        onSearch={buscar}
        notFoundContent={
          buscando ? <Spin size="small" /> : <Text type="secondary">Sin resultados</Text>
        }
        onChange={(id) => {
          const p = opciones.find((o) => o.id === Number(id));
          if (p) onElegir(p);
        }}
        options={opciones.map((p) => ({
          value: p.id,
          label: (
            <Space size={6}>
              {p.miniatura ? (
                <img className="inventario-miniatura-mini" src={p.miniatura} alt="" />
              ) : null}
              <Tag className="inventario-id-tag">#{p.id}</Tag>
              <span>{p.nombre}</span>
              {p.marca ? <Text type="secondary">· {p.marca}</Text> : null}
              {p.ubicacionNombre ? (
                <Text type="secondary">
                  ·{" "}
                  {p.ubicacionTipo === "propiedades"
                    ? formatPropertyName(p.ubicacionNombre)
                    : p.ubicacionNombre}
                </Text>
              ) : null}
              {/* Un producto dado de baja sigue siendo el mismo producto:
                  esconderlo haría que recomprarlo cree un duplicado. */}
              {p.oculto ? <Tag color="orange">dado de baja</Tag> : null}
            </Space>
          ),
        }))}
      />
    </div>
  );
};

export default RecomprarBuscador;
